import { useEffect, useRef, useState } from "react";

/**
 * § Focus: closing an overlay restores focus to the control that opened it. Radix does that for an
 * overlay that stays mounted and has its own trigger, but a host that mounts the overlay to open it
 * and unmounts it to close it (which most of ours do) is gone before it can, and focus is left on
 * the body (2026-09-21). So the opener is remembered here and put back if nothing else took focus.
 *
 * A row of a menu that opens the overlay is gone by then too, the menu having closed as it opened
 * it (2026-09-27, `useMenuOpensDialog` in the web app), so the focus goes back to the control that
 * opened the menu: the dropdown's trigger, which the menu names in `aria-labelledby`, or the trigger
 * of the quick-create bottom sheet the menu sits in, which names the sheet in `aria-controls`. The
 * row hands that control over through `holdOpener` as it is taken, so an overlay whose code arrives
 * after the menu has closed, with the focus already on the body, still finds it.
 */
export function openerOf(active: Element | null): HTMLElement | null {
  if (!(active instanceof HTMLElement) || active === document.body) return null;
  const menu = active.closest("[role='menu']");
  if (menu === null) return active;
  const labelledBy = menu.getAttribute("aria-labelledby");
  const trigger = labelledBy === null ? null : document.getElementById(labelledBy);
  if (trigger !== null) return trigger;
  const sheet = menu.closest("[data-slot='shortcut-menu-sheet'][id]");
  const sheetTrigger = sheet === null ? null : document.querySelector(`[aria-controls='${CSS.escape(sheet.id)}']`);
  return sheetTrigger instanceof HTMLElement ? sheetTrigger : null;
}

let held: HTMLElement | null = null;

/** Called by a menu row as it opens an overlay: the control focus returns to when that overlay closes, which the next overlay to open takes. */
export function holdOpener(element: HTMLElement | null): void {
  held = element;
}

function heldOpener(): HTMLElement | null {
  return held?.isConnected === true ? held : null;
}

/** The control the opening overlay returns the focus to: the one focused now, else the one a menu row held for it. Pure, so a render may call it twice. */
function openerNow(): HTMLElement | null {
  return openerOf(document.activeElement) ?? heldOpener();
}

export function useReturnFocus(open: boolean): void {
  // Read as the overlay first renders, while a menu row that opened it still holds the focus; by the time an effect runs, that menu has unmounted and the focus is on the body.
  const [atMount] = useState(() => (open ? openerNow() : null));
  const opener = useRef<HTMLElement | null>(null);
  const first = useRef(true);
  useEffect(() => {
    if (!open) return undefined;
    opener.current = first.current && atMount !== null ? atMount : openerNow();
    first.current = false;
    // The held control is this overlay's now; the next overlay starts without it.
    held = null;
    return () => {
      const element = opener.current;
      if (element !== null && element.isConnected && document.activeElement === document.body) element.focus();
    };
  }, [open, atMount]);
}
